/* PRO system cart helper (loaded in layout/theme.liquid)
   Shared by the gift guide sections (Shop bundle, Bundle builder, GWP,
   Gift finder, Countdown with products).

     ProCart.add(items, { openDrawer, source })
       Adds several products in one request: items is
       [{ id: variantId, quantity }]. Then either opens the cart drawer
       (openDrawer: true, the default; cart-drawer.js renderContents) or
       refreshes the drawer's contents and the cart count without opening it.
       Publishes PUB_SUB_EVENTS.cartUpdate (or cartError) with `source`.
       Resolves with Shopify's response; rejects with an Error whose message
       is safe to show.

     ProCart.formatMoney(cents)
       "$30" / "$30.50" in the store's currency, like money_without_trailing_zeros.

     ProCart.ERROR_MESSAGE
       The fallback message when Shopify doesn't send one.

     [data-pro-add] buttons (data-variant-id, data-source) add one of that
     variant when clicked (ProCart.addFromButton), e.g. the ADD button in
     snippets/pro-product-card.liquid.
*/

if (!window.ProCart) {
    window.ProCart = {
        ERROR_MESSAGE: 'Something went wrong. Please try again.',

        async add(items, { openDrawer = true, source = 'pro-cart' } = {}) {
            const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            let sections = [{ id: 'cart-icon-bubble' }];
            try {
                if (cart) sections = cart.getSectionsToRender();
            } catch (error) {
                sections = [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
            }
            if (openDrawer) cart?.setActiveElement?.(document.activeElement);

            const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    items,
                    sections: sections.map((section) => section.id),
                    sections_url: window.location.pathname,
                }),
            });
            const data = await response.json();

            if (data.status) {
                this.publish('cartError', {
                    source,
                    errors: data.errors || data.description,
                    message: data.message,
                });
                throw new Error(data.description || data.message || this.ERROR_MESSAGE);
            }

            this.publish('cartUpdate', { source, cartData: data });

            if (openDrawer && cart) {
                cart.renderContents(data);
            } else if (openDrawer) {
                window.location.href = window.routes?.cart_url || '/cart';
            } else {
                this.renderQuietly(data, sections, cart);
            }

            return data;
        },

        // What cart-drawer.js renderContents does, minus opening the drawer
        renderQuietly(data, sections, cart) {
            sections.forEach((section) => {
                const html = data.sections?.[section.id];
                const target = section.selector
                    ? document.querySelector(section.selector)
                    : document.getElementById(section.id);
                if (!html || !target) return;
                const source = new DOMParser()
                    .parseFromString(html, 'text/html')
                    .querySelector(section.selector || '.shopify-section');
                if (source) target.innerHTML = source.innerHTML;
            });

            if (cart) {
                cart.classList.remove('is-empty');
                cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
                // The overlay was replaced, so it needs its close listener again
                cart.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cart.close.bind(cart));
            }
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'pro-cart' } }));
        },

        publish(eventName, data) {
            if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
            publish(PUB_SUB_EVENTS[eventName], data);
        },

        formatMoney(cents) {
            try {
                return new Intl.NumberFormat(document.documentElement.lang || 'en', {
                    style: 'currency',
                    currency: window.Shopify?.currency?.active || 'USD',
                    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
                }).format(cents / 100);
            } catch (error) {
                return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
            }
        },

        // One product from a [data-pro-add] button (snippets/pro-product-card.liquid).
        // Errors show in the section's [data-pro-add-error].
        async addFromButton(button) {
            if (button.getAttribute('aria-disabled') === 'true') return;
            const errorMessage = button.closest('.pro-system')?.querySelector('[data-pro-add-error]');
            const showError = (message) => {
                if (!errorMessage) return;
                errorMessage.textContent = message;
                errorMessage.hidden = !message;
            };
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('loading');
            showError('');

            try {
                await this.add([{ id: Number(button.dataset.variantId), quantity: 1 }], {
                    source: button.dataset.source,
                });
            } catch (error) {
                console.error('Error adding a product to the cart:', error);
                showError(error.message || this.ERROR_MESSAGE);
            } finally {
                button.classList.remove('loading');
                button.removeAttribute('aria-disabled');
            }
        },
    };

    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pro-add]');
        if (button && !button.disabled) window.ProCart.addFromButton(button);
    });
}
